import type { ParseResult, ValidationIssue } from "../shared/schemas";
import { apiFetch } from "./api";

export interface ImportProgress {
  phase: "parsing" | "source" | "records" | "finalizing" | "complete";
  percent: number;
  message: string;
}

interface WorkerSuccess {
  ok: true;
  result: ParseResult;
}

interface WorkerFailure {
  ok: false;
  error: string;
}

export async function parseSchedulingFile(file: File): Promise<ParseResult> {
  const buffer = await file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL("../workers/import.worker.ts", import.meta.url), {
      type: "module",
    });
    worker.onmessage = (event: MessageEvent<WorkerSuccess | WorkerFailure>) => {
      worker.terminate();
      if (event.data.ok) resolve(event.data.result);
      else reject(new Error(event.data.error));
    };
    worker.onerror = (event) => {
      worker.terminate();
      reject(new Error(event.message || "The import worker failed."));
    };
    worker.postMessage({ fileName: file.name, buffer }, [buffer]);
  });
}

async function checksum(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function chunks<T>(values: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let index = 0; index < values.length; index += size) {
    result.push(values.slice(index, index + size));
  }
  return result;
}

export async function persistImport(
  file: File,
  displayName: string,
  parsed: ParseResult,
  onProgress: (progress: ImportProgress) => void,
): Promise<string> {
  const digest = await checksum(file);
  const created = await apiFetch<{ id: string }>("/imports", {
    method: "POST",
    body: JSON.stringify({
      fileName: file.name,
      displayName,
      contentType: file.type || "application/octet-stream",
      byteSize: file.size,
      checksum: digest,
      terms: parsed.terms,
    }),
  });

  onProgress({ phase: "source", percent: 12, message: "Archiving source file in R2..." });
  await apiFetch<void>(`/imports/${created.id}/source`, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });

  const sectionChunks = chunks(parsed.sections, 50);
  const issueChunks = chunks<ValidationIssue>(parsed.issues, 500);
  const totalChunks = Math.max(sectionChunks.length, issueChunks.length);
  for (let index = 0; index < totalChunks; index += 1) {
    const sections = sectionChunks[index] ?? [];
    const issues = issueChunks[index] ?? [];
    await apiFetch(`/imports/${created.id}/chunks`, {
      method: "POST",
      body: JSON.stringify({ sections, issues }),
    });
    onProgress({
      phase: "records",
      percent: 15 + Math.round(((index + 1) / totalChunks) * 75),
      message: `Persisting normalized records ${index + 1} of ${totalChunks}...`,
    });
  }

  onProgress({
    phase: "finalizing",
    percent: 94,
    message: "Calculating conflicts, alerts, and snapshot metrics...",
  });
  await apiFetch(`/imports/${created.id}/finalize`, {
    method: "POST",
    body: JSON.stringify({
      totalSourceRows: parsed.sourceRowCount,
      includedRows: parsed.includedRowCount,
      excludedRows: parsed.excludedRowCount,
      warningCount: parsed.issues.filter((entry) => entry.severity === "warning").length,
      errorCount: parsed.issues.filter((entry) => entry.severity === "error").length,
    }),
  });
  onProgress({ phase: "complete", percent: 100, message: "Import activated successfully." });
  return created.id;
}
